import * as React from "react";
import { useEffect, useMemo, useRef, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { css } from "@emotion/core";
import { format } from "date-fns";
import { useSinkState, useSinkActions } from "context/sink";
import { useCheckViewAccess, useCheckAdminAccess } from "context/";
import { Table, TableColumn, TableColumnConfig } from "components/common/Table";
import {
  SurveyWithExtras,
  isSurveyArchived,
  SinkOrder,
} from "../../../interfaces";
import {
  buildOrderTypeLabelsBySurveyId,
  formatOrderTypeLabelsForDisplay,
  normalizeForCompare,
} from "../../../utils/orderTypeLabel";
import { formatWGS84 } from "../../../utils/format";
import { buildSurveyDetailFields } from "../../../utils/surveyDetails";
import {
  ArchiveSurveyDialog,
  OrdersTable,
  useSurveyArchive,
} from "components/common";
import {
  Card,
  Collapse,
  Button,
  Icon,
  Tag,
  H4,
  H5,
  NonIdealState,
  Popover,
  Menu,
  MenuItem,
} from "@blueprintjs/core";
import { PERMISSION_LEVEL_ADMIN } from "../../../constants";
import useSurveyCardExpansion from "hooks/useSurveyCardExpansion";
import { useFilteredSurveys } from "hooks/useFilteredSurveys";
import useScrollPositionRestoration from "hooks/useScrollPositionRestoration";
import { useVirtualizer } from "@tanstack/react-virtual";
import { useCanDeleteTags } from "hooks/useCanDeleteTags";
import { useDownloads } from "../../../hooks/useDownloads";
import { DownloadSelector } from "components/common/DownloadSelector";
import { TagEditorSlot } from "components/common/TagEditorSlot";
import { FolderSettingsDialog } from "./FolderSettingsDialog";
import { MaterialsVolumesCharts } from "./MaterialsVolumesCharts";
import FolderAdminPermissionsDialog from "./FolderAdminPermissionsDialog";
import FolderAdminEditFolderDialog from "./FolderAdminEditFolderDialog";
import FolderAdminDeactivateDialog from "./FolderAdminDeactivateDialog";

type SurveyWithOptionalFields = SurveyWithExtras & {
  nominal_flight_date?: string | null;
  site_id?: number | null;
  folder_id?: number | null;
  locked_time?: string | null;
  status?: string | null;
  location?: { coordinates: [number, number] } | null;
  coordinates?: { latitude: number; longitude: number } | null;
  created_by_name?: string | null;
  locked_by_name?: string | null;
  created_at?: string | null;
};

type FolderRow =
  | {
      id: number;
      label: string;
      level?: number;
      newestSurveyDate: number | null;
    }
  | {
      id: "all";
      label: "All Surveys";
      newestSurveyDate: number | null;
    };

const SURVEY_ROW_CHEVRON_WIDTH = "40px";
const SURVEY_ROW_ACTIONS_WIDTH = "82px";

const SURVEY_TABLE_COLUMNS = [
  {
    key: "label",
    label: "Label",
    sortable: true,
    width: "auto",
  },
  {
    key: "nominal_flight_date",
    label: "Date",
    sortable: true,
    width: "128px",
  },
  { key: "location", label: "Site", sortable: true, width: "180px" },
  { key: "order_type", label: "Order Type", sortable: true, width: "256px" },
  { key: "status", label: "Status", sortable: true, width: "128px" },
  { key: "actions", label: "", sortable: false, width: "82px" },
] as const;

const STATUS_CONFIG = [
  { key: "created", label: "No orders", intent: "none", icon: "tick" },
  {
    key: "processing",
    label: "In-progress",
    intent: "warning",
    icon: "refresh",
  },
  { key: "processed", label: "Complete", intent: "success", icon: "tick" },
  { key: "locked", label: "Locked", intent: "success", icon: "lock" },
] as const;

const SURVEY_DATA_GRID_TEMPLATE = SURVEY_TABLE_COLUMNS.slice(0, -1)
  .map((c) => c.width)
  .join(" ");

const newestCreatedAt = (list: SurveyWithOptionalFields[]): number | null => {
  if (list.length === 0) {
    return null;
  }
  return list.reduce(
    (newest, survey) => {
      if (!survey.created_at) return newest;
      const surveyDate = new Date(survey.created_at).getTime();
      if (!newest || surveyDate > newest) {
        return surveyDate;
      }
      return newest;
    },
    null as number | null
  );
};

export const FoldersView = () => {
  const { sink, surveys } = useSinkState();
  const params = useParams<{ id?: string }>();
  const navigate = useNavigate();
  const { resink } = useSinkActions();

  const { folders } = sink;
  const [manageFolderSettings, setManageFolderSettings] = useState<{
    folder: { id: number; label: string };
  } | null>(null);
  const [manageUserAccess, setManageUserAccess] = useState<{
    id: number;
    label: string;
  } | null>(null);
  const [editFolder, setEditFolder] = useState<{
    id: number;
    label: string;
  } | null>(null);
  const [deactivateFolder, setDeactivateFolder] = useState<{
    id: number;
    label: string;
  } | null>(null);

  const newestSurveyDate = useMemo(() => {
    if (!surveys || !Array.isArray(surveys)) {
      return null;
    }
    const unarchived = surveys.filter((s) => !isSurveyArchived(s));
    return newestCreatedAt(unarchived);
  }, [surveys]);

  const foldersWithNewestDate = useMemo(() => {
    if (!folders || !Array.isArray(folders)) {
      return [];
    }
    return folders.map((folder) => {
      const folderSurveys = (surveys || []).filter(
        (s) => s.folder_id === folder.id && !isSurveyArchived(s)
      );

      return {
        ...folder,
        newestSurveyDate: newestCreatedAt(folderSurveys),
      };
    });
  }, [folders, surveys]);

  const [sorting, setSorting] = useState({
    columnKey: "label",
    ascending: true,
  });

  const sortedFolders = useMemo((): FolderRow[] => {
    const sorted = [...foldersWithNewestDate].sort((a, b) => {
      let comparison = 0;

      if (sorting.columnKey === "label") {
        const aLabel = (a.label || "").toLowerCase();
        const bLabel = (b.label || "").toLowerCase();
        comparison = aLabel < bLabel ? -1 : aLabel > bLabel ? 1 : 0;
      } else if (sorting.columnKey === "newestSurveyDate") {
        const aDate = a.newestSurveyDate || 0;
        const bDate = b.newestSurveyDate || 0;
        comparison = aDate - bDate;
      }

      return sorting.ascending ? comparison : -comparison;
    });

    const allSurveysRow: FolderRow = {
      id: "all",
      label: "All Surveys",
      newestSurveyDate: newestSurveyDate,
    };

    return [allSurveysRow, ...sorted];
  }, [foldersWithNewestDate, sorting, newestSurveyDate]);

  const handleSort = (columnKey: string, ascending: boolean) => {
    setSorting({ columnKey, ascending });
  };

  const handleRowClick = (rowIndex: number) => {
    if (rowIndex === 0) {
      navigate("/surveys");
      return;
    }
    const folder = sortedFolders[rowIndex];
    navigate(`/folders/${folder.id}`);
  };

  const renderCell = (rowIndex: number, columnIndex: number) => {
    if (rowIndex === 0) {
      if (columnIndex === 0) {
        return (
          <div
            css={css`
              display: flex;
              align-items: center;
              gap: 0.5rem;
            `}
          >
            <Icon icon="layers" color="var(--neutral-3)" />
            <span>All Surveys</span>
          </div>
        );
      } else if (columnIndex === 1) {
        if (newestSurveyDate) {
          try {
            return (
              <span>{format(new Date(newestSurveyDate), "yyyy-MM-dd")}</span>
            );
          } catch (e) {
            return <span>-</span>;
          }
        }
        return <span>-</span>;
      }
      return null;
    }

    const folderItem = sortedFolders[rowIndex];
    if (typeof folderItem.id === "string") {
      return null;
    }
    const folder = folderItem as {
      id: number;
      label: string;
      level?: number;
      newestSurveyDate: number | null;
    };
    if (columnIndex === 0) {
      return (
        <div
          css={css`
            display: flex;
            align-items: center;
            gap: 0.5rem;
          `}
        >
          <Icon icon="folder-close" color="var(--neutral-3)" />
          <span>{folder.label}</span>
        </div>
      );
    } else if (columnIndex === 1) {
      if (folder.newestSurveyDate) {
        try {
          return (
            <span>
              {format(new Date(folder.newestSurveyDate), "yyyy-MM-dd")}
            </span>
          );
        } catch (e) {
          return <span>-</span>;
        }
      }
      return <span>-</span>;
    } else if (
      columnIndex === 2 &&
      folder.level &&
      folder.level >= PERMISSION_LEVEL_ADMIN
    ) {
      return (
        <div
          css={css`
            display: flex;
            justify-content: flex-end;
          `}
          onClick={(e) => e.stopPropagation()}
        >
          <Popover
            content={
              <Menu>
                <MenuItem
                  text="Manage User Access"
                  icon="shield"
                  onClick={(e) => {
                    setManageUserAccess({ id: folder.id, label: folder.label });
                  }}
                />
                <MenuItem
                  text="Edit Folder"
                  icon="edit"
                  onClick={() =>
                    setEditFolder({ id: folder.id, label: folder.label })
                  }
                />
                <MenuItem
                  text="Deactivate"
                  icon="disable"
                  intent="danger"
                  onClick={() =>
                    setDeactivateFolder({ id: folder.id, label: folder.label })
                  }
                />
              </Menu>
            }
            position="bottom-right"
          >
            <Button icon="more" aria-label="Folder actions" />
          </Popover>
        </div>
      );
    }

    return null;
  };

  if (!folders || folders.length === 0) {
    return (
      <NonIdealState
        icon="folder-close"
        title="No Folders Yet"
        description={
          <>
            Folders help organize your surveys. To have folders created please
            contact an administrator within your organization,{" "}
            <a href="mailto:support@fybr.ca">support@fybr.ca</a> or{" "}
            <a href="mailto:sales@fybr.ca">sales@fybr.ca</a>.
          </>
        }
      />
    );
  }

  return (
    <>
      <Table
        rows={sortedFolders}
        renderer={renderCell}
        onRowClick={handleRowClick}
        onSort={handleSort}
        currentSort={sorting}
      >
        <TableColumn label="Name" width="40%" sortable sortKey="label" />
        <TableColumn
          label="Latest Survey Date"
          width="60%"
          sortable
          sortKey="newestSurveyDate"
        />
        <TableColumn label="" width="48px" sortable={false} />
      </Table>
      {manageFolderSettings && (
        <FolderSettingsDialog
          folder={manageFolderSettings.folder}
          organizationId={sink.organization?.id}
          onClose={() => setManageFolderSettings(null)}
        />
      )}
      {manageUserAccess && (
        <FolderAdminPermissionsDialog
          folder={manageUserAccess}
          onClose={() => setManageUserAccess(null)}
        />
      )}
      {editFolder && (
        <FolderAdminEditFolderDialog
          folder={editFolder}
          onClose={() => setEditFolder(null)}
          onUpdated={() => {
            resink();
          }}
        />
      )}
      {deactivateFolder && (
        <FolderAdminDeactivateDialog
          folder={deactivateFolder}
          onClose={() => setDeactivateFolder(null)}
          onDeactivated={() => {
            resink();
            if (params.id === String(deactivateFolder.id)) {
              navigate("/folders");
            }
          }}
        />
      )}
    </>
  );
};

interface SurveysViewProps {
  onCreateSurvey?: () => void;
  /**
   * Called when the visible (sorted/filtered) survey IDs change. Use for expand-all, etc.
   * May fire on every sort/filter change — keep the callback cheap (e.g. only sync to a ref).
   */
  onVisibleSurveyIdsChange?: (ids: number[]) => void;
  /** Pre-computed filtered survey lists from parent (single source of truth). */
  surveys: SurveyWithExtras[];
  folderFilteredSurveys: SurveyWithExtras[];
  filteredSurveys: SurveyWithExtras[];
  /** Whether any survey list filters are active (from useFilteredSurveys). */
  hasActiveFilters: boolean;
}

interface SurveyCardProps {
  survey: SurveyWithExtras;
  isExpanded: boolean;
  onToggle: () => void;
  sites: Array<{ id: number; label: string }>;
  folders: Array<{ id: number; label: string }>;
  isInFolderContext: boolean;
  scrollContainerRef?: React.RefObject<HTMLElement>;
}

const SurveyCard: React.FC<SurveyCardProps> = ({
  survey,
  isExpanded,
  onToggle,
  sites,
  folders,
  isInFolderContext,
  scrollContainerRef,
}) => {
  const surveyAny = survey as SurveyWithOptionalFields;

  const canDeleteTags = useCanDeleteTags();
  const hasTagEditorViewAccess = useCheckViewAccess(survey);

  const { downloads, isLoading } = useDownloads(isExpanded ? survey.id : null);
  const { resink } = useSinkActions();

  const [showDownloads, setShowDownloads] = React.useState(false);
  const [downloadButtonProps, setDownloadButtonProps] = React.useState<{
    onClick: () => void;
    text: string;
  } | null>(null);

  const handleDownloadButtonProps = React.useCallback(
    (props: { onClick: () => void; text: string }) => {
      setDownloadButtonProps((prev) =>
        prev && prev.onClick === props.onClick && prev.text === props.text
          ? prev
          : { onClick: props.onClick, text: props.text }
      );
    },
    []
  );

  React.useEffect(() => {
    if (!isExpanded) {
      setShowDownloads(false);
    }
  }, [isExpanded]);

  const getStatusInfo = () => {
    const status = surveyAny.status || "created";
    const isLocked = surveyAny.locked_time !== null;

    const statusMap: Record<string, string> = {
      created: "created",
      "in-progress": "processing",
      complete: "processed",
    };

    return {
      currentStatus: isLocked ? "locked" : statusMap[status] || "created",
      isLocked,
    };
  };

  const createdDate = surveyAny.created_at
    ? format(new Date(surveyAny.created_at), "yyyy-MM-dd")
    : "-";

  const flightDate = surveyAny.nominal_flight_date
    ? format(new Date(surveyAny.nominal_flight_date), "yyyy-MM-dd")
    : null;

  const siteName = surveyAny.site_id
    ? sites.find((site) => site.id === surveyAny.site_id)?.label
    : null;

  const locationDisplay = useMemo(() => {
    if (siteName) return siteName;
    const coords = surveyAny.location?.coordinates;
    if (coords && coords.length === 2) {
      const [lng, lat] = coords;
      return `${lat.toFixed(5)}, ${lng.toFixed(5)}`;
    }
    return "-";
  }, [siteName, surveyAny.location]);

  const folderName = surveyAny.folder_id
    ? folders.find((folder) => folder.id === surveyAny.folder_id)?.label
    : null;

  const handleToggleClick = (e: React.MouseEvent) => {
    e.stopPropagation();
    onToggle();
  };

  const status = STATUS_CONFIG.find(
    (s) => s.key === getStatusInfo().currentStatus
  );

  return (
    <Card
      css={css`
        padding: 0;

        & > div:first-of-type:hover {
          background-color: var(--neutral-6-translucent);
        }
      `}
    >
      <div
        css={css`
          display: flex;
          flex-direction: column;
          cursor: pointer;
        `}
        onClick={handleToggleClick}
      >
        {/* Row 1: Header with title, metadata, and button */}
        <div
          css={css`
            display: flex;
            justify-content: space-between;
            align-items: flex-start;
            gap: 1rem;
            padding: 0.75rem 0.75rem 0.25rem 1rem;
          `}
        >
          <div
            css={css`
              display: flex;
              align-items: flex-start;
              flex: 1;
              cursor: pointer;
            `}
          >
            <div
              css={css`
                display: flex;
                align-items: center;
                gap: 0.25rem;
                min-width: 0;
                flex: 1;
              `}
            >
              <div
                css={css`
                  display: flex;
                  gap: 0.5rem;
                  height: 100%;
                `}
              >
                <div>
                  <Button
                    icon={isExpanded ? "chevron-up" : "chevron-down"}
                    variant="minimal"
                    onClick={handleToggleClick}
                    className="survey-card-toggle"
                    css={css`
                      flex-shrink: 0;
                    `}
                  />
                </div>
              </div>
              <H4
                css={css`
                  margin: 0;
                  padding: 0 1rem;
                  overflow: hidden;
                  text-overflow: ellipsis;
                  white-space: nowrap;
                `}
              >
                {survey.label}
              </H4>
            </div>

            <div
              css={css`
                display: flex;
                flex-direction: row;
                flex-wrap: wrap;
                gap: 1rem;
              `}
            >
              <div
                css={css`
                  display: flex;
                  flex-direction: column;
                  flex: 1;
                  min-width: 128px;
                  gap: 0.25rem;
                `}
              >
                <span
                  css={css`
                    margin: 0;
                    font-size: 0.75rem;
                    font-weight: 500;
                    color: var(--neutral-4);
                    text-transform: uppercase;
                  `}
                >
                  DATE:
                </span>
                <span
                  css={css`
                    color: var(--neutral-3);
                    font-size: 0.875rem;
                    white-space: nowrap;
                  `}
                >
                  {flightDate ? flightDate : "-"}
                </span>
              </div>

              <div
                css={css`
                  display: flex;
                  flex-direction: column;
                  flex: 1;
                  min-width: 180px;
                  gap: 0.25rem;
                `}
              >
                <span
                  css={css`
                    margin: 0;
                    font-size: 0.75rem;
                    font-weight: 500;
                    color: var(--neutral-4);
                    text-transform: uppercase;
                  `}
                >
                  SITE:
                </span>
                <span
                  css={css`
                    color: var(--neutral-3);
                    font-size: 0.875rem;
                    white-space: nowrap;
                  `}
                >
                  {locationDisplay}
                </span>
              </div>

              <div
                css={css`
                  display: flex;
                  flex-direction: column;
                  flex: 1;
                  gap: 0.25rem;
                `}
              >
                <Tag
                  size="large"
                  intent={status?.intent}
                  icon={status?.icon}
                  css={css`
                    width: 120px;
                  `}
                >
                  {status?.label}
                </Tag>
              </div>
            </div>
          </div>
          <div
            css={css`
              display: flex;
              flex-direction: column;
              gap: 1rem;
            `}
          >
            <Button
              intent="primary"
              icon="send-to-map"
              endIcon="chevron-right"
              onClick={(event) => {
                event.stopPropagation();

                survey.select();
              }}
            >
              View Survey
            </Button>
          </div>
        </div>
        <div
          css={css`
            padding: 0.5rem 0.75rem 0.75rem 4rem;
            width: 100%;
          `}
        >
          {hasTagEditorViewAccess ? (
            <TagEditorSlot
              survey={surveyAny}
              canDeleteTags={canDeleteTags}
              resink={resink}
            />
          ) : null}
        </div>
      </div>
      <Collapse isOpen={isExpanded}>
        <div
          css={css`
            border-top: 1px solid var(--neutral-5);
            display: flex;
            flex-direction: column;
            gap: 1rem;
            padding-left: 3rem;
          `}
        >
          <div
            css={css`
              padding: 1rem 1rem 0 1rem;
              display: flex;
              flex-direction: row;
              flex-wrap: wrap;
              gap: 2rem;
            `}
          >
            <div
              css={css`
                display: flex;
                flex-direction: column;
                gap: 0.25rem;
                min-width: 128px;
              `}
            >
              <span
                css={css`
                  margin: 0;
                  font-size: 0.625rem;
                  font-weight: 500;
                  color: var(--neutral-4);
                  text-transform: uppercase;
                `}
              >
                CREATED BY:
              </span>
              <span
                css={css`
                  color: var(--neutral-3);
                  font-size: 0.875rem;
                  white-space: nowrap;
                `}
              >
                {surveyAny.created_by_name || "-"}
              </span>
            </div>
            <div
              css={css`
                display: flex;
                flex-direction: column;
                gap: 0.25rem;
              `}
            >
              <span
                css={css`
                  margin: 0;
                  font-size: 0.625rem;
                  font-weight: 500;
                  color: var(--neutral-4);
                  text-transform: uppercase;
                `}
              >
                LOCKED BY:
              </span>
              <span
                css={css`
                  color: var(--neutral-3);
                  font-size: 0.875rem;
                  white-space: nowrap;
                `}
              >
                {surveyAny.locked_by_name || "-"}
              </span>
            </div>
          </div>
          <div
            css={css`
              display: flex;
              flex-direction: column;
            `}
          >
            <div
              css={css`
                padding: 0 1rem 1rem 1rem;
              `}
            >
              <Button
                onClick={(e) => {
                  e.stopPropagation();
                  setShowDownloads(!showDownloads);
                }}
                aria-label={`${showDownloads ? "Hide" : "Show"} ${
                  downloads?.length || 0
                } downloads`}
                aria-expanded={showDownloads}
                css={css`
                  width: 200px;
                `}
                intent="primary"
                disabled={downloads?.length === 0}
                endIcon="cloud-download"
              >
                {showDownloads ? "Hide Downloads" : `Show Downloads`} (
                {downloads?.length || 0})
              </Button>
            </div>

            <Collapse isOpen={showDownloads}>
              <div
                css={css`
                  display: flex;
                  flex-direction: column;
                `}
              >
                <div
                  css={css`
                    max-height: 240px;
                    overflow-y: auto;
                    padding: 1rem;
                    border-top: 1px solid var(--neutral-5);
                    border-bottom: 1px solid var(--neutral-5);
                  `}
                >
                  <DownloadSelector
                    downloads={downloads || []}
                    isLoading={isLoading}
                    variant="inline"
                    hideButton={true}
                    onButtonProps={handleDownloadButtonProps}
                  />
                </div>

                {downloadButtonProps && downloads && downloads.length > 0 && (
                  <div
                    css={css`
                      display: flex;
                      justify-content: flex-end;
                      padding: 1rem;
                    `}
                  >
                    <Button
                      icon="download"
                      onClick={downloadButtonProps.onClick}
                      intent="primary"
                      text={downloadButtonProps.text}
                    />
                  </div>
                )}
              </div>
            </Collapse>
          </div>
        </div>
      </Collapse>
    </Card>
  );
};

/** Wrapper that computes filtered surveys once and passes them to SurveysView. Use when SurveysView is rendered without a parent that already has the data (e.g. route element). */
export const SurveysViewWithData: React.FC<
  Omit<
    SurveysViewProps,
    "surveys" | "folderFilteredSurveys" | "filteredSurveys" | "hasActiveFilters"
  >
> = (props) => {
  const { surveys, folderFilteredSurveys, filteredSurveys, hasActiveFilters } =
    useFilteredSurveys();
  return (
    <SurveysView
      {...props}
      surveys={surveys ?? []}
      folderFilteredSurveys={folderFilteredSurveys}
      filteredSurveys={filteredSurveys}
      hasActiveFilters={hasActiveFilters}
    />
  );
};

export const SurveysView: React.FC<SurveysViewProps> = ({
  onCreateSurvey,
  onVisibleSurveyIdsChange,
  surveys,
  folderFilteredSurveys,
  filteredSurveys,
  hasActiveFilters,
}) => {
  const { surveyListFilters, sink, loading } = useSinkState();
  const params = useParams<{ id?: string }>();
  const sites = sink.sites || [];
  const folders = sink.folders || [];
  const isInFolderContext = !!params.id && params.id !== "all";

  const orderTypeLabelsBySurveyId = useMemo(() => {
    return buildOrderTypeLabelsBySurveyId(sink.orders ?? []);
  }, [sink.orders]);

  const ordersBySurveyId = useMemo(() => {
    const map = new Map<number, SinkOrder[]>();
    (sink.orders ?? []).forEach((order: SinkOrder) => {
      const surveyId = order?.survey_id;
      if (typeof surveyId !== "number") return;
      const existing = map.get(surveyId);
      if (existing) existing.push(order);
      else map.set(surveyId, [order]);
    });
    return map;
  }, [sink.orders]);

  const [sorting, setSorting] = useState({
    columnKey: "nominal_flight_date",
    ascending: false,
  });

  const cardExpansion = useSurveyCardExpansion();

  const getSurveyStatusKey = (survey: SurveyWithOptionalFields) => {
    const status = survey.status || "created";
    const isLocked = (survey as SurveyWithOptionalFields).locked_time !== null;
    const statusMap: Record<string, string> = {
      created: "created",
      "in-progress": "processing",
      complete: "processed",
    };
    return isLocked ? "locked" : statusMap[status] || "created";
  };

  const sortedSurveys = useMemo(() => {
    const statusOrder = ["created", "processing", "processed", "locked"];

    const getSortValue = (survey: SurveyWithExtras) => {
      const surveyAny = survey as SurveyWithOptionalFields;

      switch (sorting.columnKey) {
        case "label":
          return (survey.label || "").toLowerCase();
        case "created_at":
          return surveyAny.created_at
            ? new Date(surveyAny.created_at).getTime()
            : 0;
        case "nominal_flight_date":
          return surveyAny.nominal_flight_date
            ? new Date(surveyAny.nominal_flight_date).getTime()
            : 0;
        case "folder": {
          const folderName =
            surveyAny.folder_id &&
            folders.find((folder) => folder.id === surveyAny.folder_id)?.label;
          return (folderName || "").toLowerCase();
        }
        case "site": {
          const siteName =
            surveyAny.site_id &&
            sites.find((site) => site.id === surveyAny.site_id)?.label;
          return (siteName || "").toLowerCase();
        }
        case "location": {
          const siteName =
            surveyAny.site_id &&
            sites.find((site) => site.id === surveyAny.site_id)?.label;
          if (siteName) return siteName.toLowerCase();

          const coords = surveyAny.location?.coordinates;
          if (coords && coords.length === 2) {
            const [lng, lat] = coords;
            return `${lat.toFixed(5)}, ${lng.toFixed(5)}`;
          }

          return "-";
        }
        case "status": {
          const statusKey = getSurveyStatusKey(surveyAny);
          const index = statusOrder.indexOf(statusKey);
          return index === -1 ? statusOrder.length : index;
        }
        case "order_type": {
          const labels = orderTypeLabelsBySurveyId.get(survey.id) ?? [];
          return normalizeForCompare(formatOrderTypeLabelsForDisplay(labels));
        }
        default:
          return "";
      }
    };

    return [...filteredSurveys].sort((a, b) => {
      const aValue = getSortValue(a);
      const bValue = getSortValue(b);

      let comparison = 0;

      if (sorting.columnKey === "nominal_flight_date") {
        const aSurvey = a as SurveyWithOptionalFields;
        const bSurvey = b as SurveyWithOptionalFields;
        const aHasDate = !!aSurvey.nominal_flight_date;
        const bHasDate = !!bSurvey.nominal_flight_date;

        if (!aHasDate && !bHasDate) {
          const aCreated = aSurvey.created_at
            ? new Date(aSurvey.created_at).getTime()
            : 0;
          const bCreated = bSurvey.created_at
            ? new Date(bSurvey.created_at).getTime()
            : 0;
          return sorting.ascending ? aCreated - bCreated : bCreated - aCreated;
        }
        if (!aHasDate) return 1;
        if (!bHasDate) return -1;

        const aDate = new Date(aSurvey.nominal_flight_date!).getTime();
        const bDate = new Date(bSurvey.nominal_flight_date!).getTime();
        comparison = aDate - bDate;
        if (comparison === 0) {
          const aLabel = (a.label || "").toLowerCase();
          const bLabel = (b.label || "").toLowerCase();
          comparison = aLabel.localeCompare(bLabel);
        }
        return sorting.ascending ? comparison : -comparison;
      }

      if (sorting.columnKey === "location") {
        const aString = String(aValue || "");
        const bString = String(bValue || "");
        const aEmpty = !aString || aString === "-";
        const bEmpty = !bString || bString === "-";

        if (aEmpty && bEmpty) {
          comparison = 0;
        } else if (aEmpty) {
          comparison = sorting.ascending ? 1 : -1; // END for A-Z, START for Z-A
        } else if (bEmpty) {
          comparison = sorting.ascending ? -1 : 1; // END for A-Z, START for Z-A
        } else {
          comparison = sorting.ascending
            ? aString.localeCompare(bString)
            : bString.localeCompare(aString);
        }

        if (comparison === 0) {
          const aLabel = (a.label || "").toLowerCase();
          const bLabel = (b.label || "").toLowerCase();
          comparison = sorting.ascending
            ? aLabel.localeCompare(bLabel)
            : bLabel.localeCompare(aLabel);
        }

        return comparison;
      }

      if (typeof aValue === "number" && typeof bValue === "number") {
        comparison = aValue - bValue;
      } else {
        const aString = String(aValue);
        const bString = String(bValue);
        comparison = aString.localeCompare(bString);
      }

      if (comparison === 0) {
        const aLabel = (a.label || "").toLowerCase();
        const bLabel = (b.label || "").toLowerCase();
        comparison = aLabel.localeCompare(bLabel);
      }

      return sorting.ascending ? comparison : -comparison;
    });
  }, [filteredSurveys, sorting, folders, sites, orderTypeLabelsBySurveyId]);

  const visibleSurveyIds = useMemo(
    () => sortedSurveys.map((s) => s.id),
    [sortedSurveys]
  );

  const prevVisibleSurveyIdsRef = useRef<number[]>([]);
  useEffect(() => {
    if (!onVisibleSurveyIdsChange) return;
    const prev = prevVisibleSurveyIdsRef.current;
    if (
      prev.length === visibleSurveyIds.length &&
      visibleSurveyIds.every((id, i) => id === prev[i])
    ) {
      return;
    }
    prevVisibleSurveyIdsRef.current = visibleSurveyIds;
    onVisibleSurveyIdsChange(visibleSurveyIds);
  }, [visibleSurveyIds, onVisibleSurveyIdsChange]);

  const handleSort = (columnKey: string, ascending?: boolean) => {
    if (typeof ascending === "boolean") {
      setSorting({ columnKey, ascending });
      return;
    }
    const newAscending =
      sorting.columnKey === columnKey ? !sorting.ascending : true;
    setSorting({ columnKey, ascending: newAscending });
  };

  if (!surveys || surveys.length === 0) {
    return (
      <NonIdealState
        icon="map"
        title="No Surveys Yet"
        description="Create your first survey to begin collecting data."
        action={
          onCreateSurvey && (
            <Button
              icon="map-create"
              text="Create Survey"
              intent="primary"
              onClick={onCreateSurvey}
            />
          )
        }
      />
    );
  }

  if (hasActiveFilters && filteredSurveys.length === 0) {
    return (
      <NonIdealState
        icon="map"
        title="No Surveys Match Your Filters"
        description="Try adjusting your filters or clear them."
      />
    );
  }

  if (isInFolderContext && folderFilteredSurveys.length === 0) {
    const folder = folders.find((f) => f.id === parseInt(params.id || "0", 10));
    return (
      <NonIdealState
        icon="map"
        title={
          folder
            ? `No Surveys in "${folder.label}"`
            : "No Surveys in This Folder"
        }
        description="Create a survey in this folder to get started."
        action={
          onCreateSurvey && (
            <Button
              icon="map-create"
              text="Create Survey"
              intent="primary"
              onClick={onCreateSurvey}
            />
          )
        }
      />
    );
  }

  if (filteredSurveys.length === 0) {
    return (
      <NonIdealState
        icon="map"
        title="No Surveys Found"
        description="No surveys match your current view."
      />
    );
  }

  return (
    <div
      css={css`
        flex: 1;
        display: flex;
        flex-direction: column;
        height: 100%;
        overflow: hidden;
      `}
    >
      <div
        css={css`
          flex: 1;
          display: flex;
          flex-direction: column;
          overflow: hidden;
        `}
      >
        <SurveysTableView
          surveys={sortedSurveys}
          cardExpansion={cardExpansion}
          sites={sites}
          folders={folders}
          orderTypeLabelsBySurveyId={orderTypeLabelsBySurveyId}
          ordersBySurveyId={ordersBySurveyId}
          sorting={sorting}
          onSort={handleSort}
          isInFolderContext={isInFolderContext}
        />
      </div>
    </div>
  );
};

interface DetailFieldProps {
  label: string;
  value: React.ReactNode;
  dimmed?: boolean;
}

const DetailField: React.FC<DetailFieldProps> = ({ label, value, dimmed }) => (
  <div
    css={css`
      display: flex;
      flex-direction: column;
      gap: 0.25rem;
      min-width: 128px;
    `}
  >
    <span
      css={css`
        margin: 0;
        font-size: 0.625rem;
        font-weight: 500;
        color: var(--neutral-4);
        text-transform: uppercase;
      `}
    >
      {label}
    </span>
    <span
      css={css`
        color: ${dimmed ? "var(--neutral-4)" : "var(--neutral-3)"};
        font-size: 0.875rem;
        white-space: nowrap;
      `}
    >
      {value}
    </span>
  </div>
);

const SectionHeader: React.FC<{ children: React.ReactNode }> = ({
  children,
}) => (
  <div
    css={css`
      margin-bottom: 0.75rem;
      font-size: 0.875em;
      font-weight: 700;
      color: var(--neutral-4);
      text-transform: uppercase;
      letter-spacing: 0.04em;
    `}
  >
    {children}
  </div>
);

interface OrdersListProps {
  orders: SinkOrder[];
}

const OrdersList: React.FC<OrdersListProps> = ({ orders }) => {
  if (orders.length === 0) return null;

  return (
    <div
      css={css`
        border-top: 1px solid var(--neutral-7);
        padding: 0.75rem 1rem 0 1.5rem;
      `}
    >
      <SectionHeader>Orders</SectionHeader>
      <OrdersTable orders={orders} />
    </div>
  );
};

interface SurveyTableRowProps {
  survey: SurveyWithExtras;
  isExpanded: boolean;
  onToggle: () => void;
  downloadsOpen: boolean;
  onToggleDownloads: () => void;
  sites: Array<{ id: number; label: string }>;
  folders: Array<{ id: number; label: string }>;
  isInFolderContext: boolean;
  orderTypeLabel: string | null;
  orders: SinkOrder[];
}

const SurveyTableRow: React.FC<SurveyTableRowProps> = ({
  survey,
  isExpanded,
  onToggle,
  downloadsOpen: showDownloads,
  onToggleDownloads,
  sites,
  folders,
  isInFolderContext,
  orderTypeLabel,
  orders,
}) => {
  const surveyAny = survey as SurveyWithOptionalFields;
  const isArchived = isSurveyArchived(survey);

  const coordinatesFormatted = surveyAny.coordinates
    ? formatWGS84(surveyAny.coordinates)
    : null;
  const coordinatesDisplay = coordinatesFormatted
    ? `${coordinatesFormatted.latitude}, ${coordinatesFormatted.longitude}`
    : "-";

  const canDeleteTags = useCanDeleteTags();
  const hasTagEditorViewAccess = useCheckViewAccess(survey);
  const hasAdminAccess = useCheckAdminAccess(survey);

  const { downloads, isLoading } = useDownloads(isExpanded ? survey.id : null);
  const { resink, closeSurveyListDownloadsOpen } = useSinkActions();
  const [downloadButtonProps, setDownloadButtonProps] = React.useState<{
    onClick: () => void;
    text: string;
  } | null>(null);
  const archive = useSurveyArchive(survey);

  const handleDownloadButtonProps = React.useCallback(
    (props: { onClick: () => void; text: string }) => {
      setDownloadButtonProps((prev) =>
        prev && prev.onClick === props.onClick && prev.text === props.text
          ? prev
          : { onClick: props.onClick, text: props.text }
      );
    },
    []
  );

  React.useEffect(() => {
    if (!isExpanded) {
      closeSurveyListDownloadsOpen(survey.id);
    }
  }, [isExpanded, survey.id, closeSurveyListDownloadsOpen]);

  const getStatusInfo = () => {
    const status = surveyAny.status || "created";
    const isLocked = surveyAny.locked_time !== null;

    const statusMap: Record<string, string> = {
      created: "created",
      "in-progress": "processing",
      complete: "processed",
    };

    return {
      currentStatus: isLocked ? "locked" : statusMap[status] || "created",
      isLocked,
    };
  };

  const flightDate = surveyAny.nominal_flight_date
    ? format(new Date(surveyAny.nominal_flight_date), "yyyy-MM-dd")
    : null;

  const siteName = surveyAny.site_id
    ? sites.find((site) => site.id === surveyAny.site_id)?.label
    : null;

  const locationDisplay = useMemo(() => {
    if (siteName) return siteName;
    const coords = surveyAny.location?.coordinates;
    if (coords && coords.length === 2) {
      const [lng, lat] = coords;
      return `${lat.toFixed(5)}, ${lng.toFixed(5)}`;
    }
    return "-";
  }, [siteName, surveyAny.location]);

  const folderName = surveyAny.folder_id
    ? folders.find((folder) => folder.id === surveyAny.folder_id)?.label
    : null;

  const handleToggleClick = (e: React.MouseEvent) => {
    e.stopPropagation();
    onToggle();
  };

  const handleRowClick = () => {
    survey.select();
  };

  const status = STATUS_CONFIG.find(
    (s) => s.key === getStatusInfo().currentStatus
  );

  return (
    <div
      css={css`
        padding: 0;
        border-top: 1px solid var(--neutral-6);
        border-bottom: 1px solid var(--neutral-6);
        background-color: var(--neutral-6-translucent);
        margin-bottom: ${isExpanded ? "0.5rem" : "0"};
        display: flex;
        flex-direction: column;
        opacity: ${isArchived ? 0.65 : 1};

        ${isExpanded ? "background-color: var(--neutral-6);" : ""}

        &:hover {
          background-color: var(--neutral-6);
        }
      `}
    >
      <div
        css={css`
          display: flex;
          flex-direction: row;
        `}
      >
        <button
          type="button"
          css={css`
            width: ${SURVEY_ROW_CHEVRON_WIDTH};
            align-self: stretch;
            flex-shrink: 0;
            background-color: transparent;
            border: none;
            display: flex;
            justify-content: center;
            align-items: flex-start;
            padding-top: 12px;
            padding-left: 20px;
            color: var(--neutral-4);

            &:hover {
              color: var(--neutral-1);
            }

            cursor: pointer;
          `}
          onClick={handleToggleClick}
        >
          <Icon
            icon="chevron-right"
            size={24}
            css={css`
              transition: transform 150ms ease;
              transform: rotate(${isExpanded ? 90 : 0}deg);

              @media (prefers-reduced-motion: reduce) {
                transition: none;
              }
            `}
          />
        </button>
        <div
          css={css`
            flex: 1;
            min-width: 0;
            display: flex;
            flex-direction: column;
            cursor: pointer;
          `}
          onClick={handleRowClick}
        >
          <div
            css={css`
              display: grid;
              grid-template-columns: ${SURVEY_DATA_GRID_TEMPLATE};
              align-items: center;
              gap: 0.5rem;
              padding: 0.5rem 0;
            `}
          >
            <div
              css={css`
                min-width: 0;
                padding: 0 1.5rem;
                display: flex;
                align-items: center;
                gap: 0.5rem;
              `}
            >
              <H5
                css={css`
                  margin: 0;
                  overflow: hidden;
                  text-overflow: ellipsis;
                  white-space: nowrap;
                `}
              >
                {survey.label}
              </H5>
              {isArchived && (
                <Tag intent="none" minimal>
                  Archived
                </Tag>
              )}
            </div>
            <span>{flightDate ? flightDate : "-"}</span>
            <span>{locationDisplay}</span>
            <span
              css={css`
                overflow: hidden;
                text-overflow: ellipsis;
                white-space: nowrap;
                padding-right: 0.5rem;
              `}
              title={orderTypeLabel ?? undefined}
            >
              {orderTypeLabel ?? "-"}
            </span>
            <Tag
              size="large"
              intent={status?.intent}
              icon={status?.icon}
              css={css`
                justify-self: start;
                width: 128px;
              `}
            >
              {status?.label}
            </Tag>
          </div>
          <div
            css={css`
              padding: 0 calc(1.5rem - 8px) calc(1rem - 8px) calc(1.25rem - 8px);
              width: 100%;
            `}
          >
            {hasTagEditorViewAccess ? (
              <TagEditorSlot
                survey={surveyAny}
                canDeleteTags={canDeleteTags}
                resink={resink}
              />
            ) : null}
          </div>
        </div>
        {hasAdminAccess ? (
          <div
            css={css`
              width: ${SURVEY_ROW_ACTIONS_WIDTH};
              align-self: stretch;
              flex-shrink: 0;
              display: flex;
              justify-content: center;
              align-items: flex-start;
              padding-top: 8px;
            `}
            onClick={(e) => e.stopPropagation()}
          >
            <Popover
              content={
                <Menu>
                  {isArchived ? (
                    <MenuItem
                      text="Restore"
                      icon="unarchive"
                      disabled={archive.isRestoring}
                      onClick={archive.restore}
                    />
                  ) : (
                    <MenuItem
                      text="Archive"
                      icon="archive"
                      intent="danger"
                      onClick={archive.open}
                    />
                  )}
                </Menu>
              }
              position="bottom-right"
            >
              <Button icon="more" aria-label="Survey actions" />
            </Popover>
            <ArchiveSurveyDialog entityName={survey.label} {...archive} />
          </div>
        ) : (
          <div
            css={css`
              width: ${SURVEY_ROW_ACTIONS_WIDTH};
              flex-shrink: 0;
            `}
          />
        )}
      </div>
      <Collapse isOpen={isExpanded} transitionDuration={0}>
        <div
          onClick={(e) => e.stopPropagation()}
          css={css`
            border-top: 1px solid var(--neutral-5);
            background-color: #252a31;
            display: flex;
            flex-direction: column;
            gap: 1.25rem;
          `}
        >
          <div
            css={css`
              padding: 1rem 1rem 0 1.5rem;
            `}
          >
            <SectionHeader>Survey Details</SectionHeader>
            <div
              css={css`
                display: flex;
                flex-direction: row;
                flex-wrap: wrap;
                gap: 2rem;
              `}
            >
              {buildSurveyDetailFields(surveyAny).map((field) => (
                <DetailField
                  key={field.key}
                  label={`${field.label}:`}
                  value={field.value}
                  dimmed={field.key === "id"}
                />
              ))}
              <DetailField label="COORDINATES:" value={coordinatesDisplay} />
            </div>
          </div>
          <OrdersList orders={orders} />
          <div
            css={css`
              display: flex;
              flex-direction: column;
            `}
          >
            <div
              css={css`
                padding: 0 1rem 1rem 1.5rem;
              `}
            >
              <SectionHeader>Downloads</SectionHeader>
              <Button
                onClick={(e) => {
                  e.stopPropagation();
                  onToggleDownloads();
                }}
                aria-label={`${showDownloads ? "Hide" : "Show"} ${
                  downloads?.length || 0
                } downloads`}
                aria-expanded={showDownloads}
                css={css`
                  width: 200px;
                `}
                intent="primary"
                disabled={downloads?.length === 0}
                endIcon="cloud-download"
              >
                {showDownloads ? "Hide Downloads" : `Show Downloads`} (
                {downloads?.length || 0})
              </Button>
            </div>

            <Collapse isOpen={showDownloads} transitionDuration={0}>
              <div
                css={css`
                  display: flex;
                  flex-direction: column;
                `}
              >
                <div
                  css={css`
                    max-height: 240px;
                    overflow-y: auto;
                    padding: 1rem 1.5rem;
                    border-top: 1px solid var(--neutral-7);
                    border-bottom: 1px solid var(--neutral-7);
                  `}
                >
                  <DownloadSelector
                    downloads={downloads || []}
                    isLoading={isLoading}
                    variant="inline"
                    hideButton={true}
                    onButtonProps={handleDownloadButtonProps}
                  />
                </div>

                {downloadButtonProps && downloads && downloads.length > 0 && (
                  <div
                    css={css`
                      display: flex;
                      justify-content: flex-end;
                      padding: 1rem 1.5rem;
                    `}
                  >
                    <Button
                      icon="download"
                      onClick={downloadButtonProps.onClick}
                      intent="primary"
                      text={downloadButtonProps.text}
                    />
                  </div>
                )}
              </div>
            </Collapse>
          </div>
          <div>
            <div
              css={css`
                padding: 0 1rem 0 1.5rem;
              `}
            >
              <SectionHeader>Survey Data</SectionHeader>
            </div>
            <MaterialsVolumesCharts
              surveyId={survey.id}
              currentSurveyId={survey.id}
              siteId={surveyAny.site_id}
            />
          </div>
        </div>
      </Collapse>
    </div>
  );
};

interface SurveysTableViewProps {
  surveys: SurveyWithExtras[];
  cardExpansion: ReturnType<typeof useSurveyCardExpansion>;
  sites: Array<{ id: number; label: string }>;
  folders: Array<{ id: number; label: string }>;
  orderTypeLabelsBySurveyId: Map<number, string[]>;
  ordersBySurveyId: Map<number, SinkOrder[]>;
  sorting: {
    columnKey: string;
    ascending: boolean;
  };
  onSort: (columnKey: string, ascending?: boolean) => void;
  isInFolderContext: boolean;
}

const SurveysTableView: React.FC<SurveysTableViewProps> = ({
  surveys,
  cardExpansion,
  sites,
  folders,
  orderTypeLabelsBySurveyId,
  ordersBySurveyId,
  sorting,
  onSort,
  isInFolderContext,
}) => {
  const { loading, surveyListDownloadsOpen } = useSinkState();
  const { toggleSurveyListDownloadsOpen } = useSinkActions();
  const tableScrollContainerRef = React.useRef<HTMLDivElement>(null);
  useScrollPositionRestoration(tableScrollContainerRef, !loading);

  const rowVirtualizer = useVirtualizer({
    count: surveys.length,
    getScrollElement: () => tableScrollContainerRef.current,
    estimateSize: (index) =>
      cardExpansion.isExpanded(surveys[index].id) ? 680 : 132,
    getItemKey: (index) => surveys[index].id,
    overscan: 5,
  });

  const columns: TableColumnConfig[] = SURVEY_TABLE_COLUMNS.slice(0, -1).map(
    (column, index) => ({
      ...column,
      index,
      sortKey: column.key,
    })
  );

  return (
    <div
      css={css`
        display: flex;
        flex-direction: column;
        height: 100%;
        min-height: 0;
      `}
    >
      <div
        css={css`
          display: flex;
          flex-direction: row;
          flex-shrink: 0;
        `}
      >
        <div
          css={css`
            width: ${SURVEY_ROW_CHEVRON_WIDTH};
            flex-shrink: 0;
          `}
        />
        <div
          css={css`
            flex: 1;
            min-width: 0;
            display: grid;
            grid-template-columns: ${SURVEY_DATA_GRID_TEMPLATE};
            align-items: center;
            gap: 0.5rem;
            cursor: pointer;
            background: transparent;
          `}
        >
          {columns.map((column) => {
            const isSorted = sorting && sorting.columnKey === column.sortKey;
            const isSortable = column.sortable && onSort;

            return (
              <div
                key={column.index}
                css={css`
                  ${column.width ? `width: ${column.width};` : "flex: 1;"}
                  min-width: 128px;
                  font-weight: 700;
                  font-size: 0.9rem;
                  display: flex;
                  padding: ${column.index === 0 ? "0.5rem 1rem" : "0.5rem 0"};
                  align-items: center;
                  gap: 0.5rem;
                  ${isSortable ? "cursor: pointer;" : ""}

                  color: ${isSorted ? "var(--neutral-3)" : "var(--neutral-4)"};

                  &:hover {
                    color: var(--neutral-3);
                  }
                `}
                onClick={() => {
                  if (isSortable) {
                    const newAscending =
                      isSorted && sorting?.ascending ? false : true;
                    onSort?.(column.sortKey || "", newAscending);
                  }
                }}
              >
                <span
                  css={css`
                    padding: ${column.index === 0 ? "0 0.5rem" : "0"};
                    user-select: none;
                  `}
                >
                  {column.label}
                </span>
                {isSorted && (
                  <Icon
                    icon={sorting?.ascending ? "chevron-up" : "chevron-down"}
                    size={14}
                  />
                )}
              </div>
            );
          })}
        </div>
        <div
          css={css`
            width: ${SURVEY_ROW_ACTIONS_WIDTH};
            flex-shrink: 0;
          `}
        />
      </div>
      <div
        ref={tableScrollContainerRef}
        css={css`
          display: flex;
          flex-direction: column;
          flex: 1;
          min-height: 0;
          overflow-y: auto;
        `}
      >
        <div
          style={{
            height: rowVirtualizer.getTotalSize(),
            position: "relative",
            width: "100%",
          }}
        >
          {rowVirtualizer.getVirtualItems().map((vi) => {
            const survey = surveys[vi.index];
            return (
              <div
                key={vi.key}
                data-index={vi.index}
                ref={rowVirtualizer.measureElement}
                style={{
                  position: "absolute",
                  top: 0,
                  left: 0,
                  width: "100%",
                  transform: `translateY(${vi.start}px)`,
                }}
              >
                <SurveyTableRow
                  survey={survey}
                  isExpanded={cardExpansion.isExpanded(survey.id)}
                  onToggle={() => cardExpansion.toggle(survey.id)}
                  downloadsOpen={surveyListDownloadsOpen[survey.id] ?? false}
                  onToggleDownloads={() =>
                    toggleSurveyListDownloadsOpen(survey.id)
                  }
                  sites={sites}
                  folders={folders}
                  isInFolderContext={isInFolderContext}
                  orderTypeLabel={formatOrderTypeLabelsForDisplay(
                    orderTypeLabelsBySurveyId.get(survey.id) ?? []
                  )}
                  orders={ordersBySurveyId.get(survey.id) ?? []}
                />
              </div>
            );
          })}
        </div>
      </div>
    </div>
  );
};
